import {
  createContext,
  createEffect,
  createMemo,
  onCleanup,
  onMount,
  useContext,
  type Accessor,
  type ParentComponent,
} from 'solid-js';
import { createStore } from 'solid-js/store';
import type { ManagedWindow, OpenWindowInput, WindowBounds } from '../types/window';

const FALLBACK_TASKBAR_HEIGHT = 34;
const MIN_VISIBLE_TITLE = 96;

export interface WindowManager {
  windows: ManagedWindow[];
  activeWindowId: Accessor<string | undefined>;
  openWindow(input: OpenWindowInput): void;
  focusWindow(id: string): void;
  closeWindow(id: string): void;
  minimizeWindow(id: string): void;
  restoreWindow(id: string): void;
  toggleMaximize(id: string): void;
  setBounds(id: string, bounds: WindowBounds): void;
  closeAll(): void;
}

const WindowManagerContext = createContext<WindowManager>();

function taskbarHeight(): number {
  const configured = Number.parseFloat(
    window.getComputedStyle(document.documentElement).getPropertyValue('--taskbar-height'),
  );
  return Number.isFinite(configured) && configured > 0 ? configured : FALLBACK_TASKBAR_HEIGHT;
}

function workspaceSize() {
  return {
    width: Math.max(320, window.innerWidth),
    height: Math.max(240, window.innerHeight - taskbarHeight()),
  };
}

function clampBounds(bounds: WindowBounds, minWidth: number, minHeight: number): WindowBounds {
  const workspace = workspaceSize();
  const width = Math.min(Math.max(bounds.width, minWidth), workspace.width);
  const height = Math.min(Math.max(bounds.height, minHeight), workspace.height);
  const x = Math.min(Math.max(bounds.x, -width + MIN_VISIBLE_TITLE), workspace.width - MIN_VISIBLE_TITLE);
  const y = Math.min(Math.max(bounds.y, 0), Math.max(0, workspace.height - 28));
  return { x, y, width, height };
}

export const WindowManagerProvider: ParentComponent = (props) => {
  const [windows, setWindows] = createStore<ManagedWindow[]>([]);
  let topZ = 20;
  let cascade = 0;

  const activeWindowId = createMemo(() => {
    const active = windows
      .filter((item) => !item.minimized)
      .reduce<ManagedWindow | undefined>((best, item) => (!best || item.zIndex > best.zIndex ? item : best), undefined);
    return active?.id;
  });

  const findIndex = (id: string) => windows.findIndex((item) => item.id === id);

  function focusWindow(id: string) {
    const index = findIndex(id);
    if (index < 0) return;
    topZ += 1;
    setWindows(index, { zIndex: topZ, minimized: false });
  }

  function openWindow(input: OpenWindowInput) {
    const existingIndex = findIndex(input.id);
    if (existingIndex >= 0) {
      focusWindow(input.id);
      return;
    }

    const workspace = workspaceSize();
    const minWidth = input.minWidth ?? 320;
    const minHeight = input.minHeight ?? 220;
    const width = Math.min(input.width ?? 720, workspace.width - 16);
    const height = Math.min(input.height ?? 520, workspace.height - 16);
    const offset = (cascade++ % 7) * 24;
    const desiredBounds = {
      x: Math.max(8, Math.round((workspace.width - width) / 2) + offset - 72),
      y: Math.max(8, Math.round((workspace.height - height) / 2) + offset - 48),
      width,
      height,
    };

    topZ += 1;
    setWindows(windows.length, {
      id: input.id,
      kind: input.kind,
      title: input.title,
      icon: input.icon,
      bounds: clampBounds(desiredBounds, minWidth, minHeight),
      minimized: false,
      maximized: false,
      zIndex: topZ,
      resizable: input.resizable ?? true,
      minWidth,
      minHeight,
      projectSlug: input.projectSlug,
    });
  }

  function closeWindow(id: string) {
    const index = findIndex(id);
    if (index >= 0) setWindows((items) => items.filter((_, itemIndex) => itemIndex !== index));
  }

  function minimizeWindow(id: string) {
    const index = findIndex(id);
    if (index >= 0) setWindows(index, 'minimized', true);
  }

  function restoreWindow(id: string) {
    const index = findIndex(id);
    if (index < 0) return;
    topZ += 1;
    setWindows(index, { minimized: false, zIndex: topZ });
  }

  function toggleMaximize(id: string) {
    const index = findIndex(id);
    if (index < 0 || !windows[index].resizable) return;
    const current = windows[index];
    if (current.maximized) {
      setWindows(index, {
        maximized: false,
        bounds: current.restoreBounds || current.bounds,
        restoreBounds: undefined,
      });
    } else {
      setWindows(index, {
        maximized: true,
        restoreBounds: current.bounds,
      });
    }
    focusWindow(id);
  }

  function setBoundsForWindow(id: string, bounds: WindowBounds) {
    const index = findIndex(id);
    if (index < 0 || windows[index].maximized) return;
    setWindows(index, 'bounds', clampBounds(bounds, windows[index].minWidth, windows[index].minHeight));
  }

  function closeAll() {
    setWindows([]);
  }

  function clampAllWindows() {
    windows.forEach((item, index) => {
      if (!item.maximized) {
        setWindows(index, 'bounds', clampBounds(item.bounds, item.minWidth, item.minHeight));
      }
    });
  }

  onMount(() => window.addEventListener('resize', clampAllWindows));
  onCleanup(() => window.removeEventListener('resize', clampAllWindows));

  createEffect(() => {
    // Touch the store so Solid keeps activeWindowId synchronized after structural updates.
    windows.length;
    activeWindowId();
  });

  const manager: WindowManager = {
    windows,
    activeWindowId,
    openWindow,
    focusWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    toggleMaximize,
    setBounds: setBoundsForWindow,
    closeAll,
  };

  return <WindowManagerContext.Provider value={manager}>{props.children}</WindowManagerContext.Provider>;
};

export function useWindowManager(): WindowManager {
  const manager = useContext(WindowManagerContext);
  if (!manager) throw new Error('useWindowManager must be used inside WindowManagerProvider');
  return manager;
}
